/**
 * Vista admin para subir archivos de universo PAC y monitorear su procesamiento.
 * Cada upload se respalda en S3 y dispara un job que el scheduler consume.
 */

import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';

interface UniverseFile {
  id: string;
  s3Key: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  uploadedAt: string;
  processedAt: string | null;
  stats: Record<string, unknown> | null;
  errorMessage: string | null;
}

interface UniverseList {
  data: UniverseFile[];
  page: number;
  pageSize: number;
  total: number;
}

const PAGE_SIZE = 20;
const REFRESH_INTERVAL_MS = 8_000;

const STATUS_LABEL: Record<UniverseFile['status'], { label: string; className: string }> = {
  pending: { label: 'En cola', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  processing: { label: 'Procesando', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  completed: { label: 'Completado', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  failed: { label: 'Falló', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
};

const fmtDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function PacUniversoPage() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<UniverseList | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      const url = `${getAdminEndpointUrl('pacUniverse', 'list')}?${params.toString()}`;
      const result = await adminHttpClient.get<UniverseList>(url);
      setData(result);
    } catch (err) {
      console.error('Error cargando universos:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  // Auto-refresh si hay archivos en proceso
  useEffect(() => {
    if (!data) return;
    const anyInFlight = data.data.some((f) => f.status === 'pending' || f.status === 'processing');
    if (!anyInFlight) return;
    const id = setInterval(() => void fetchList(), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [data, fetchList]);

  const handleFileSelect = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    try {
      const signedRes = await adminHttpClient.post<{ uploadUuid: string; endpoint: string }>(getAdminEndpointUrl('pacUniverse', 'uploadUrl'), {});
      const uploadRes = await fetch(signedRes.endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'text/plain' },
        body: file,
      });
      if (!uploadRes.ok) throw new Error(`Error ${uploadRes.status} subiendo archivo`);

      await adminHttpClient.post(getAdminEndpointUrl('pacUniverse', 'register'), { uploadUuid: signedRes.uploadUuid });

      void fetchList();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Error subiendo el archivo');
    } finally {
      setUploading(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Universo de mandatos PAC</h1>
          <p className="mt-1 text-sm text-slate-500">
            Sube el archivo de universo BCI. Cada archivo queda respaldado en S3 y el scheduler aplica los cambios al maestro de mandatos. Los reintentos y el sync con Lups se procesan en cola.
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h2 className="text-base font-medium">Subir nuevo archivo</h2>
          <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/40">
            <span className="material-symbols-outlined text-base">upload_file</span>
            {uploading ? 'Subiendo…' : 'Seleccionar archivo BCI'}
            <input
              type="file"
              accept=".txt,text/plain"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFileSelect(file);
                e.target.value = '';
              }}
            />
          </label>
          {uploadError && <p className="mt-2 text-xs text-red-600">{uploadError}</p>}
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
              <tr>
                <th className="px-4 py-3">Subido</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Procesado</th>
                <th className="px-4 py-3">Stats</th>
                <th className="px-4 py-3">Error</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && !data ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Cargando…
                  </td>
                </tr>
              ) : !data || data.data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Sin archivos subidos.
                  </td>
                </tr>
              ) : (
                data.data.map((f) => {
                  const cfg = STATUS_LABEL[f.status];
                  return (
                    <tr key={f.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="px-4 py-3 text-xs">{fmtDateTime(f.uploadedAt)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${cfg.className}`}>{cfg.label}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{f.processedAt ? fmtDateTime(f.processedAt) : '—'}</td>
                      <td className="px-4 py-3 text-xs">
                        {f.stats ? (
                          <pre className="max-w-md whitespace-pre-wrap font-mono text-[11px] text-slate-700 dark:text-slate-300">{JSON.stringify(f.stats, null, 2)}</pre>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-xs text-xs text-red-600">{f.errorMessage ?? '—'}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const url = getAdminEndpointUrl('pacUniverse', 'downloadUrl').replace('{id}', f.id);
                              const res = await adminHttpClient.get<{ url: string }>(url);
                              window.open(res.url, '_blank', 'noopener,noreferrer');
                            } catch (err) {
                              console.error(err);
                            }
                          }}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Descargar
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {data && data.total > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-xs dark:border-slate-800">
              <span className="text-slate-500">
                Página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50 dark:border-slate-700">
                  Anterior
                </button>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50 dark:border-slate-700">
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
